import { useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { DeliveryAreasPanel } from "./DeliveryAreasPanel";
import { Lbl } from "./AreaLists";
import { OrderStepsCard } from "./OrderStepsCard";

const FEE_MODES = [["bairros", "Por bairro", "Lista de bairros com taxa; o cliente escolhe o bairro."], ["mapa", "Por mapa", "Raios a partir da loja e áreas desenhadas; o endereço do cliente é localizado no mapa."]];
const KEYS = ["delivery_enabled", "delivery_area_mode", "store_location", "delivery_pin_adjust", "courier_route_enabled", "order_steps"];

function ZonesList({ zones, onChange }) {
  const patch = (i, k, v) => onChange(zones.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="card-surface p-5 space-y-2" data-testid="delivery-zones">
      <p className="eyebrow">Regiões de entrega (taxa por bairro)</p>
      {zones.map((z, i) => (
        <div key={i} className="flex items-end gap-2">
          <Lbl t="Bairro" className="min-w-0 flex-1"><Input value={z.name} placeholder="Ex: Centro" onChange={(e) => patch(i, "name", e.target.value)} data-testid={`zone-name-${i}`} /></Lbl>
          <Lbl t="Taxa (R$)" className="shrink-0"><Input type="number" value={z.fee} placeholder="0,00" className="w-32" onChange={(e) => patch(i, "fee", e.target.value)} data-testid={`zone-fee-${i}`} /></Lbl>
          <Button size="icon" variant="ghost" onClick={() => onChange(zones.filter((_, j) => j !== i))} data-testid={`zone-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...zones, { name: "", fee: "" }])} data-testid="add-zone-btn"><Plus className="h-4 w-4 mr-1" />Adicionar bairro</Button>
    </div>
  );
}

function toPayload(s) {
  const out = Object.fromEntries(KEYS.map((k) => [k, s[k]]));
  out.min_order = Number(s.min_order) || 0; out.default_eta = Number(s.default_eta) || 0; out.delivery_fee = Number(s.delivery_fee) || 0;
  out.delivery_zones = (s.delivery_zones || []).filter((z) => z.name).map((z) => ({ name: z.name, fee: Number(z.fee) || 0 }));
  out.delivery_radii = (s.delivery_radii || []).filter((r) => Number(r.km) > 0).map((r) => ({ km: Number(r.km), fee: Number(r.fee) || 0, eta: Number(r.eta) || 0 }));
  out.delivery_areas = (s.delivery_areas || []).filter((a) => a.name?.trim() && a.points.length >= 3).map((a) => ({ ...a, fee: Number(a.fee) || 0, eta: Number(a.eta) || 0 }));
  return out;
}

export function DeliverySettingsPanel() {
  const { session, refresh } = useAuth();
  const t = session.tenant;
  const [s, setS] = useState({ ...t.settings });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const settings = toPayload(s);
    if (settings.delivery_areas.length < (s.delivery_areas || []).length) toast.warning("Áreas sem nome ou com menos de 3 pontos não foram salvas");
    setBusy(true);
    try { await api.put("/settings/delivery", { settings }); await refresh(); toast.success("Área de entrega e taxas salvas"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="delivery-settings-panel">
      <div className="card-surface p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <label className="flex items-center justify-between rounded-lg border px-4 text-sm">Delivery ativo<Switch checked={s.delivery_enabled} onCheckedChange={(v) => set("delivery_enabled", v)} data-testid="delivery-enabled-switch" /></label>
        <Field label="Pedido mínimo (R$)"><Input type="number" value={s.min_order} onChange={(e) => set("min_order", e.target.value)} data-testid="min-order-input" /></Field>
        <Field label="Prazo padrão (min)"><Input type="number" value={s.default_eta} onChange={(e) => set("default_eta", e.target.value)} data-testid="default-eta-input" /></Field>
        <Field label="Taxa padrão (R$)" hint={s.delivery_area_mode === "mapa" ? "Pedidos internos sem localização" : "Usada se não houver bairros"}><Input type="number" value={s.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} data-testid="menu-delivery-fee-input" /></Field>
        <label className="sm:col-span-2 flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm">
          <span>Rota do entregador<span className="block text-xs text-muted-foreground">Libera o botão para juntar vários pedidos em uma rota única</span></span>
          <Switch checked={!!s.courier_route_enabled} onCheckedChange={(v) => set("courier_route_enabled", v)} data-testid="courier-route-switch" />
        </label>
      </div>
      <div className="card-surface p-5 space-y-3" data-testid="fee-mode">
        <p className="eyebrow">Como cobrar a entrega</p>
        <div className="grid sm:grid-cols-2 gap-2">{FEE_MODES.map(([k, l, d]) => (
          <button key={k} type="button" onClick={() => set("delivery_area_mode", k)} className={`rounded-lg border p-3 text-left transition-colors ${(s.delivery_area_mode || "bairros") === k ? "border-primary bg-primary/10" : "hover:bg-muted"}`} data-testid={`fee-mode-${k}`}>
            <p className="font-semibold text-sm">{l}</p><p className="text-xs text-muted-foreground">{d}</p>
          </button>
        ))}</div>
      </div>
      {s.delivery_area_mode === "mapa" ? <DeliveryAreasPanel s={s} set={set} tenant={t} /> : <ZonesList zones={s.delivery_zones || []} onChange={(v) => set("delivery_zones", v)} />}
      <OrderStepsCard steps={s.order_steps || {}} onChange={(v) => set("order_steps", v)} />
      <div className="sticky bottom-3 z-10 flex justify-end">
        <Button size="lg" onClick={save} disabled={busy} className="shadow-lg" data-testid="save-delivery-settings-btn"><Save className="h-4 w-4 mr-2" />Salvar área de entrega e taxas</Button>
      </div>
    </div>
  );
}
